import { createSelector } from "@reduxjs/toolkit";
import { selectSearchState } from "../../selectors/shared";
import {
  isFacetPriceRange,
  getSelectedPriceRangeRefinement,
} from "../../priceRange/selectors";
import { COLOUR_FACET_ID } from "../../../../facet/constants";
export { default as getAllAppliedRefinements } from "./getAllAppliedRefinements";
export { getSort } from "../../query/selectors";

const emptyObject = {};
const emptyArray = [];

export const selectSearchFiltersDomain = (state) => {
  const search = selectSearchState(state);

  return {
    facets: search.facets ?? emptyArray,
    selectedFacets: search.selectedFacets ?? emptyArray,
    selectedSort: search.selectedSort ?? emptyObject,
    dismissedFloors: search.dismissedFloors ?? emptyObject,
  };
};

export const hasSearchFacets = (state) =>
  selectSearchFiltersDomain(state).facets.length > 0;

export const getSelectedValuesForFacet = (state, facet) => {
  const selectedFacetValues = [];
  const { selectedFacets } = selectSearchFiltersDomain(state);

  selectedFacets
    .filter((selectedFacet) => selectedFacet.facetId === facet.id)
    .forEach((selectedValue) => {
      const currentFacetValueIndex = selectedFacetValues.findIndex(
        (facetValue) => facetValue.id === selectedValue.valueId,
      );

      const currentFacetValue = facet.facetValues.find(
        (facetValue) => facetValue.id === selectedValue.valueId,
      );
      if (currentFacetValueIndex > -1) {
        selectedFacetValues[currentFacetValueIndex] = {
          ...currentFacetValue,
          isSelected: selectedValue.isSelected,
        };
      } else {
        selectedFacetValues.push({
          ...currentFacetValue,
          isSelected: selectedValue.isSelected,
        });
      }
    });

  return selectedFacetValues;
};

const selectFacets = (state) => selectSearchFiltersDomain(state).facets;
const selectSelectedFacets = (state) =>
  selectSearchFiltersDomain(state).selectedFacets;
const selectPriceRangeState = (state) =>
  state.search?.range?.currentprice ?? emptyObject;

const getFacetSelectionLookup = createSelector(
  [selectSelectedFacets],
  (selectedFacets) =>
    selectedFacets.reduce((facetSelectionLookup, selection) => {
      const { facetId, valueId, isSelected } = selection;
      const selectionsForFacet = facetSelectionLookup.get(facetId) ?? new Map();

      selectionsForFacet.set(valueId, isSelected);
      facetSelectionLookup.set(facetId, selectionsForFacet);

      return facetSelectionLookup;
    }, new Map()),
);

const getFacetValuesWithSelections = (facetValues, facetSelectionLookup) =>
  facetValues.map((facetValue) => {
    const isSelected = facetSelectionLookup?.get(facetValue.id);

    return isSelected === undefined
      ? facetValue
      : {
          ...facetValue,
          isSelected,
        };
  });

const getFacetHasSelectedValues = (
  facet,
  facetValues,
  facetSelectionLookup,
  hasSelectedPriceRange,
) => {
  if (isFacetPriceRange(facet)) {
    return hasSelectedPriceRange;
  }

  if (!facetSelectionLookup) {
    return facet.hasSelectedValues;
  }

  return facetValues.some(({ isSelected }) => isSelected);
};

export const hasSelectedFacets = createSelector([selectFacets], (facets) =>
  facets.some(({ hasSelectedValues }) => hasSelectedValues),
);

export const getFacetsWithSelections = createSelector(
  [selectFacets],
  (facets) => facets.filter((facet) => facet.hasSelectedValues),
);

const getSelectedPriceRangeRefinementValue = (state) =>
  state.search?.range?.currentprice
    ? getSelectedPriceRangeRefinement(state)
    : null;

export const getSelectedFacetValues = createSelector(
  [getFacetsWithSelections, getSelectedPriceRangeRefinementValue],
  (selectedFacets, selectedPriceRangeFacet) => {
    const selectedFacetValues = selectedFacets
      .filter((facet) => !isFacetPriceRange(facet))
      .flatMap((item) =>
        item.facetValues.map((facetValue) => ({
          ...facetValue,
          facetId: item.id,
        })),
      )
      .filter((facetValue) => facetValue.isSelected === true);

    return selectedPriceRangeFacet
      ? selectedFacetValues.concat({
          ...selectedPriceRangeFacet,
          facetId: "currentprice",
        })
      : selectedFacetValues;
  },
);

export const hasFacets = (state) => state.search.facets.length > 0;

const filterZeroCountOrNotSelected = (facetValues) =>
  facetValues.filter(
    (facetValue) => facetValue.count > 0 || facetValue.isSelected,
  );

export const getVisibleFacets = createSelector(
  [selectFacets, getFacetSelectionLookup, selectPriceRangeState],
  (facets, facetSelectionLookup, priceRangeState) => {
    const hasSelectedPriceRange = Boolean(priceRangeState.hasSelectedValues);

    return facets.map((facet) => {
      const selectionsForFacet = facetSelectionLookup.get(facet.id);
      const facetValuesWithSelections = getFacetValuesWithSelections(
        facet.facetValues,
        selectionsForFacet,
      );
      const filteredFacetValues = filterZeroCountOrNotSelected(
        facetValuesWithSelections,
      );
      const hasSelectedValues = getFacetHasSelectedValues(
        facet,
        facetValuesWithSelections,
        selectionsForFacet,
        hasSelectedPriceRange,
      );

      return {
        ...facet,
        facetValues: filteredFacetValues,
        hasSelectedValues,
      };
    });
  },
);

export const hasColourFacet = createSelector([selectFacets], (facets) =>
  facets.some(({ id }) => id === COLOUR_FACET_ID),
);

export const hasActiveFacetFilters = createSelector(
  [selectFacets, getFacetSelectionLookup, selectPriceRangeState],
  (facets, facetSelectionLookup, priceRangeState) => {
    const hasSelectedPriceRange = Boolean(priceRangeState.hasSelectedValues);

    return facets.some((facet) => {
      const facetValuesWithSelections = getFacetValuesWithSelections(
        facet.facetValues,
        facetSelectionLookup.get(facet.id),
      );

      return getFacetHasSelectedValues(
        facet,
        facetValuesWithSelections,
        facetSelectionLookup.get(facet.id),
        hasSelectedPriceRange,
      );
    });
  },
);

export const getFacetSelectedValueNames = (facet) => {
  const { hasSelectedValues, facetValues } = facet;

  return hasSelectedValues
    ? facetValues
        .filter(({ isSelected }) => isSelected)
        .map(({ name }) => name)
        .join(", ")
    : "";
};

export const getFacetSelectedCount = (facet) => {
  const { hasSelectedValues, facetValues } = facet;

  return hasSelectedValues
    ? facetValues.filter(({ isSelected }) => isSelected).length
    : 0;
};
